// ✅ Tous les imports en haut
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import ClientModel from "../models/ClientModel";

// ✅ Base URL API
export const base_url = "http://localhost:8000/api/v1/";



export function useGetClients(customToken = null) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 5,
    current_page: 1,
    last_page: 1,
  });

  // ✅ Si pas de token fourni, on prend celui du user connecté
  const userToken = useSelector((state) => state.auth.token);
  const token = customToken || userToken;

  const navigate = useNavigate();

  const fetchClients = useCallback(
    async (customPage = 1) => {
      if (!token) {
        navigate("/login");
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(
          `${base_url}clients?page=${customPage}&per_page=${pagination.per_page}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) throw new Error("Erreur lors de la récupération des clients");

        const data = await response.json();
        const clientsFormatés = data.clients.map((item) => new ClientModel(item));

        setClients(clientsFormatés);
        setPagination({
          total: data.total,
          per_page: data.per_page,
          current_page: data.current_page,
          last_page: Math.ceil(data.total / data.per_page),
        });
      } catch (error) {
        console.error("Erreur:", error);
        setClients([]);
      } finally {
        setLoading(false);
      }
    },
    [token, navigate, pagination.per_page]
  );

  useEffect(() => {
    fetchClients(page);
  }, [fetchClients, page]);

  return {
    clients,
    loading,
    pagination,
    page,
    setPage,
    fetchClients,
    setClients,
    setLoading,
    setPagination,
  };
}
// ✅ Hook personnalisé pour récupérer les clients avec pagination
export async function getClient(token) {
  if (!token) throw new Error("Utilisateur non authentifié");

  try {
    const response = await fetch(`${base_url}getclient`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des clients");
    }

    const data = await response.json();
    return data.clients || [];
  } catch (err) {
    console.error("Erreur getClient:", err);
    return [];
  }
}


// ✅ Fonction séparée pour ajouter un client
export async function addClient(clientData) {
  const token = localStorage.getItem("authTokenUtilisateur");
  if (!token) throw new Error("Utilisateur non authentifié");

  // Transforme toutes les chaînes vides en null sauf nom_affichage
  const payload = {
    nom_affichage: clientData.nom_affichage?.trim(), // obligatoire
    nom_contact: clientData.nom_contact?.trim() || null,
    email: clientData.email?.trim() || null,
    telephone: clientData.telephone?.trim() || null,
    adresse_ligne1: clientData.adresse_ligne1?.trim() || null,
    adresse_ligne2: clientData.adresse_ligne2?.trim() || null,
    code_postal: clientData.code_postal?.trim() || null,
    ville: clientData.ville?.trim() || null,
    siret: clientData.siret?.trim() || null,
    numero_tva: clientData.numero_tva?.trim() || null,
    notes: clientData.notes?.trim() || null,
  };

  //console.log("Payload JSON envoyé au backend :", payload);

  const response = await fetch(`${base_url}clients`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  let data;
  try {
    data = await response.json(); // parse automatique
  } catch (err) {
    console.error("Erreur parsing JSON :", err);
    throw new Error("Le serveur a renvoyé une réponse invalide");
  }

  if (!response.ok) throw new Error(data.message || "Erreur lors de l'ajout du client");

  //console.log("Client ajouté :", data.client);
  return new ClientModel(data.client);
}
export async function updateClient(clientData) {
  const token = localStorage.getItem("authTokenUtilisateur");
  if (!token) throw new Error("Utilisateur non authentifié");

  const payload = {
    nom_affichage: clientData.nom_affichage?.trim(),
    nom_contact: clientData.nom_contact?.trim() || null,
    email: clientData.email?.trim() || null,
    telephone: clientData.telephone?.trim() || null,
    adresse_ligne1: clientData.adresse_ligne1?.trim() || null,
    adresse_ligne2: clientData.adresse_ligne2?.trim() || null,
    code_postal: clientData.code_postal?.trim() || null,
    ville: clientData.ville?.trim() || null,
    siret: clientData.siret?.trim() || null,
    numero_tva: clientData.numero_tva?.trim() || null,
    notes: clientData.notes?.trim() || null,
  };

  const response = await fetch(`${base_url}clients/${clientData.id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  let data;
  try {
    data = await response.json();
  } catch (err) {
    console.error("Erreur parsing JSON :", err);
    throw new Error("Le serveur a renvoyé une réponse invalide");
  }

  if (!response.ok) throw new Error(data.message || "Erreur lors de la mise à jour du client");

  return new ClientModel(data.client);
}

// Fonction pour supprimer un client
export async function deleteClient(clientId) {
  const token = localStorage.getItem("authTokenUtilisateur");
  if (!token) throw new Error("Utilisateur non authentifié");

  const response = await fetch(`${base_url}clients/${clientId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  let data;
  try {
    data = await response.json();
  } catch (err) {
    console.error("Erreur parsing JSON :", err);
    throw new Error("Le serveur a renvoyé une réponse invalide");
  }

  if (!response.ok) throw new Error(data.message || "Erreur lors de la suppression du client");

  return data; // renvoie { success: true, message: 'Client supprimé avec succès' }
}



